import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Spinner } from "@fluentui/react-components";
import { Box24Regular } from "@fluentui/react-icons";
import { cancelRawMaterialControl, deleteRawMaterialControl, DownloadRawMaterialControlPdf, GetRawMaterialControl } from "../../../services/RawMaterialService";
import { useRawMaterialControl } from "../redux/ReduxContext";
import { FurnaceType, RawMaterial, RawMaterialControl } from "../redux/rawMaterialControl";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { forgetOiLastRoute } from "../lastRoute";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { fadeIn } from "../EventReport/OiForm";
import { useToast } from "../../ToastProvider";
import { FileReference } from "../OiReport";
import PowderPieChart from "../PowderOrder/PowderPieChart";


interface OiPowderOrderProps {
    onOpenFileGallery: (files: FileReference[], initialIndex: number) => void;
}

function formatGrindTime(hours: number | undefined, minutes: number | undefined) {
    return `${String(hours ?? 0)} h ${String(minutes ?? 0).padStart(2, "0")} min`;
}

function formatDecimalValue(value: number | string | undefined) {
    return String(value ?? "").replace(".", ",");
}

type CompositionSortKey = keyof RawMaterial | "ratio";
type CalculationSortKey =
    | "raw_mtrl"
    | "lot"
    | "trim"
    | "totalRatio"
    | "pegRatio"
    | "recycledRatio"
    | `selected:${string}`
    | `impurity:${string}`;

type SortableCalculationValue = {
    value: number | string;
    hasValue: boolean;
};

export default function RawMaterialControlRead({ onOpenFileGallery }: OiPowderOrderProps) {

    const { state, dispatch } = useRawMaterialControl();
    const [orderId, setOrderId] = React.useState("");
    const [createdAt, setCreatedAt] = React.useState("");
    const [updatedAt, setUpdatedAt] = React.useState("");
    const [isDraft, setIsDraft] = React.useState(false);
    const [isCancelled, setIsCancelled] = React.useState(false);
    const { showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { uuid } = useParams<{ uuid: string }>();
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [cancelDialog, setCancelDialog] = useState(false);
    const [selectedCompositionMaterial, setSelectedCompositionMaterial] = useState<RawMaterial | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [showImpurities, setShowImpurities] = useState(false);
    const calculationScrollRef = useRef<HTMLDivElement | null>(null);
    const calculationScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const calculationDragStateRef = useRef<{ startX: number; startThumbLeft: number } | null>(null);
    const [calculationThumbWidth, setCalculationThumbWidth] = useState(0);
    const [calculationThumbLeft, setCalculationThumbLeft] = useState(0);
    const [isCalculationThumbDragging, setIsCalculationThumbDragging] = useState(false);
    const compositionScrollRef = useRef<HTMLDivElement | null>(null);
    const compositionScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const compositionDragStateRef = useRef<{ startX: number; startThumbLeft: number } | null>(null);
    const [compositionThumbWidth, setCompositionThumbWidth] = useState(0);
    const [compositionThumbLeft, setCompositionThumbLeft] = useState(0);
    const [isCompositionThumbDragging, setIsCompositionThumbDragging] = useState(false);

    const [compositionSortConfig, setCompositionSortConfig] = useState<{
        key: CompositionSortKey | null;
        direction: "asc" | "desc";
    }>({
        key: null,
        direction: "asc",
    });

    const [calculationSortConfig, setCalculationSortConfig] = useState<{
        key: CalculationSortKey | null;
        direction: "asc" | "desc";
    }>({
        key: null,
        direction: "asc",
    });

    const handleCompositionSort = (key: CompositionSortKey) => {
        let direction: "asc" | "desc" = "asc";
        if (compositionSortConfig.key === key && compositionSortConfig.direction === "asc") {
            direction = "desc";
        }

        setCompositionSortConfig({ key, direction });
    };

    const handleCalculationSort = (key: CalculationSortKey) => {
        let direction: "asc" | "desc" = "asc";
        if (calculationSortConfig.key === key && calculationSortConfig.direction === "asc") {
            direction = "desc";
        }

        setCalculationSortConfig({ key, direction });
    };

    const normalizeValue = (value: unknown): number | string => {
        if (value === null || value === undefined) return "";

        if (typeof value === "number") return value;

        if (typeof value === "string") {
            const num = Number(value);
            return isNaN(num) ? value.toLowerCase() : num;
        }

        return "";
    };

    const hasChemicalComposition = (material: RawMaterial) => Boolean(material.powderComposition?.length);

    const openCompositionDialog = (material: RawMaterial) => {
        if (!hasChemicalComposition(material)) {
            return;
        }

        setSelectedCompositionMaterial(material);
    };
    const getRatioValue = (material: RawMaterial) => {
        if (material.raw_mtrl?.includes("PRZ")) {
            return Number(material.recycledRatio);
        } else if (material.raw_mtrl?.includes("PEG")) {
            return Number(material.pegRatio);
        } else {
            return Number(material.ratioPercent);
        }
    };

    const ratio = (material: RawMaterial) => {
        return (
            <td className={`text-right ${textYellowBlue}`}>
                {formatNumber(getRatioValue(material))}
            </td>
        );
    };

    const formatNumber = (value: number) => {
        if (isNaN(value)) return "-";
        return value.toFixed(4).replace(".", ",");
    };

    const formatPercentDisplay = (value: number | string | null | undefined) => {
        if (value === null || value === undefined || value === "") return "-";

        const numericValue =
            typeof value === "number"
                ? value
                : Number(String(value).replace(",", "."));

        if (isNaN(numericValue)) {
            return String(value);
        }

        // If it's extremely small but not zero → show scientific notation
        if (Math.abs(numericValue) < 0.0001 && numericValue !== 0) {
            return numericValue.toExponential(2).replace(".", ",");
        }

        if (numericValue.toFixed(4) === "0.0000") {
            return "-";
        }

        return numericValue.toFixed(4).replace(".", ",");
    };

    const calculationTotalRatioEnabled = () => {
        const materialCounts: Record<string, number> = {};

        state.materials.forEach((row) => {
            if (row.raw_mtrl) {
                materialCounts[row.raw_mtrl] = (materialCounts[row.raw_mtrl] || 0) + 1;
            }
        });

        return Object.values(materialCounts).some((count) => count > 1);
    };

    const calculationPegRatioEnabled = () => {
        return state.materials.some((row) => row.raw_mtrl?.includes("PEG"));
    };

    const calculationRecycledRatioEnabled = () => {
        return state.materials.some((row) => row.raw_mtrl?.includes("PRZ"));
    };

    const actualRatioTotal = () => {
        const achievedTotal = Object.values(state.achieved_pct ?? {}).reduce((sum, value) => sum + (value ?? 0), 0);
        const impuritiesTotal = state.materials.reduce((sum, material) => {
            return sum + (material.impurityExpansion?.reduce((acc, item) => acc + (item.contribution_to_batch_pct ?? 0), 0) ?? 0);
        }, 0);

        return achievedTotal + impuritiesTotal;
    };

    const impurityTotalForElement = (element: string) => {
        return state.materials.reduce((sum, material) => {
            const impurity = material.impurityExpansion?.find((item) => item.element === element);
            return sum + (impurity?.contribution_to_batch_pct ?? 0);
        }, 0);
    };

    const calculationRatioColumns = {
        total: calculationTotalRatioEnabled(),
        peg: calculationPegRatioEnabled(),
        recycled: calculationRecycledRatioEnabled(),
    };

    const calculationColumnWidths = {
        raw_mtrl: "140px",
        lot: "100px",
        trim: "40px",
        totalRatio: "70px",
        pegRatio: "70px",
        recycledRatio: "90px",
        selectedElement: "70px",
        impurityElement: "70px",
        action: "80px",
    } as const;

    const calculationColumnCount =
        3 +
        Number(calculationRatioColumns.total) +
        Number(calculationRatioColumns.peg) +
        Number(calculationRatioColumns.recycled) +
        state.selectedElements.length +
        (showImpurities ? (state.responseElements?.length ?? 0) : 0);

    const hasCalculationResults =
        state.materials.length > 0 ||
        Boolean(state.targetPercents?.length) ||
        Boolean(state.responseElements?.length) ||
        Object.keys(state.achieved_pct ?? {}).length > 0;

    const sortedMaterials = [...state.materials].sort((a, b) => {
        if (!compositionSortConfig.key) return 0;

        let aValue: unknown;
        let bValue: unknown;

        if (compositionSortConfig.key === "ratio") {
            aValue = getRatioValue(a);
            bValue = getRatioValue(b);
        } else {
            aValue = a[compositionSortConfig.key];
            bValue = b[compositionSortConfig.key];
        }

        const normalizedA = normalizeValue(aValue);
        const normalizedB = normalizeValue(bValue);

        const isANaN = typeof normalizedA === "number" && isNaN(normalizedA);
        const isBNaN = typeof normalizedB === "number" && isNaN(normalizedB);

        if (isANaN && isBNaN) return 0;
        if (isANaN) return 1;
        if (isBNaN) return -1;

        if (normalizedA < normalizedB) return compositionSortConfig.direction === "asc" ? -1 : 1;
        if (normalizedA > normalizedB) return compositionSortConfig.direction === "asc" ? 1 : -1;

        return 0;
    });

    const getCalculationSortValue = (material: RawMaterial, key: CalculationSortKey): SortableCalculationValue => {
        switch (key) {
            case "raw_mtrl":
                return { value: material.raw_mtrl ?? "", hasValue: Boolean(material.raw_mtrl) };
            case "lot":
                return { value: material.lot ?? "", hasValue: Boolean(material.lot) };
            case "trim":
                return {
                    value: material.trim_element ?? "",
                    hasValue: material.role === "trim" && Boolean(material.trim_element),
                };
            case "totalRatio": {
                const isDuplicateMaterial = state.materials.filter((row) => row.raw_mtrl === material.raw_mtrl).length > 1;
                const ratioValue = Number(material.ratio);
                return {
                    value: ratioValue,
                    hasValue: isDuplicateMaterial && !isNaN(ratioValue),
                };
            }
            case "pegRatio": {
                const pegValue = Number(material.pegRatio);
                return {
                    value: pegValue,
                    hasValue: Boolean(material.raw_mtrl?.includes("PEG")) && !isNaN(pegValue),
                };
            }
            case "recycledRatio": {
                const recycledValue = Number(material.recycledRatio);
                return {
                    value: recycledValue,
                    hasValue: Boolean(material.raw_mtrl?.includes("PRZ")) && !isNaN(recycledValue),
                };
            }
            default:
                if (key.startsWith("selected:")) {
                    const element = key.slice("selected:".length);
                    const selectedValue = Number(material.contributionPercent?.[element]);
                    return {
                        value: selectedValue,
                        hasValue: !isNaN(selectedValue),
                    };
                }

                if (key.startsWith("impurity:")) {
                    const element = key.slice("impurity:".length);
                    const impurityValue = Number(material.impurityExpansion?.find((item) => item.element === element)?.contribution_to_batch_pct);
                    return {
                        value: impurityValue,
                        hasValue: !isNaN(impurityValue),
                    };
                }

                return { value: "", hasValue: false };
        }
    };

    const sortedCalculationMaterials = [...state.materials].sort((a, b) => {
        if (!calculationSortConfig.key) return 0;

        const aSortValue = getCalculationSortValue(a, calculationSortConfig.key);
        const bSortValue = getCalculationSortValue(b, calculationSortConfig.key);

        if (aSortValue.hasValue !== bSortValue.hasValue) {
            return aSortValue.hasValue ? -1 : 1;
        }

        if (!aSortValue.hasValue && !bSortValue.hasValue) {
            return 0;
        }

        const normalizedA = normalizeValue(aSortValue.value);
        const normalizedB = normalizeValue(bSortValue.value);

        const isANaN = typeof normalizedA === "number" && isNaN(normalizedA);
        const isBNaN = typeof normalizedB === "number" && isNaN(normalizedB);

        if (isANaN && isBNaN) return 0;
        if (isANaN) return 1;
        if (isBNaN) return -1;

        if (normalizedA < normalizedB) return calculationSortConfig.direction === "asc" ? -1 : 1;
        if (normalizedA > normalizedB) return calculationSortConfig.direction === "asc" ? 1 : -1;

        return 0;
    });

    const updateCalculationScrollbar = useCallback(() => {
        const scrollEl = calculationScrollRef.current;
        const trackEl = calculationScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;

        const { scrollWidth, clientWidth, scrollLeft } = scrollEl;
        const trackWidth = trackEl.clientWidth;

        if (scrollWidth <= clientWidth) {
            setCalculationThumbWidth(0);
            setCalculationThumbLeft(0);
            return;
        }

        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 32);
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;

        setCalculationThumbWidth(thumbWidth);
        setCalculationThumbLeft(thumbLeft);
    }, []);

    const updateCompositionScrollbar = useCallback(() => {
        const scrollEl = compositionScrollRef.current;
        const trackEl = compositionScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;

        const { scrollWidth, clientWidth, scrollLeft } = scrollEl;
        const trackWidth = trackEl.clientWidth;

        if (scrollWidth <= clientWidth) {
            setCompositionThumbWidth(0);
            setCompositionThumbLeft(0);
            return;
        }

        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 32);
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;

        setCompositionThumbWidth(thumbWidth);
        setCompositionThumbLeft(thumbLeft);
    }, []);



    useEffect(() => {
        if (!uuid) return;
        let cancelled = false;
        setIsLoading(true);

        const fallBackToOverview = () => {
            forgetOiLastRoute(location.pathname);
            showToast({ title: 'Raw material control not found', intent: 'error', body: 'The order could not be loaded and may have been removed.' });
            navigate('/Oi', { replace: true });
        };

        GetRawMaterialControl(uuid).then(report => {
            if (cancelled) return;
            if (!report) {
                fallBackToOverview();
                return;
            }
            const data: RawMaterialControl = JSON.parse(report.jsonData || "{}");
            data.orderId = report.orderId || "";
            setOrderId(report.orderId || "");
            setCreatedAt(report.createdAt || "");
            setUpdatedAt(report.lastUpdated || "");
            setIsDraft(report.isDraft || false);
            setIsCancelled(report.isCancelled || false);
            dispatch({ type: 'HYDRATE', payload: data });
            setIsLoading(false);
        }).catch(error => {
            if (cancelled) return;
            console.error('Failed to load raw material control', error);
            fallBackToOverview();
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, uuid]);

    useEffect(() => {
        const scrollEl = calculationScrollRef.current;
        if (!scrollEl) return;

        updateCalculationScrollbar();
        const handleScroll = () => updateCalculationScrollbar();

        scrollEl.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', updateCalculationScrollbar);

        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', updateCalculationScrollbar);
        };
    }, [
        isLoading,
        showImpurities,
        state.materials.length,
        state.selectedElements.length,
        state.responseElements?.length,
        updateCalculationScrollbar,
    ]);

    useEffect(() => {
        updateCalculationScrollbar();
    }, [
        isLoading,
        showImpurities,
        state.materials.length,
        state.selectedElements.length,
        state.responseElements?.length,
        updateCalculationScrollbar,
    ]);

    useEffect(() => {
        const scrollEl = compositionScrollRef.current;
        if (!scrollEl) return;

        updateCompositionScrollbar();
        const handleScroll = () => updateCompositionScrollbar();

        scrollEl.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', updateCompositionScrollbar);

        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', updateCompositionScrollbar);
        };
    }, [
        isLoading,
        state.materials.length,
        updateCompositionScrollbar,
    ]);

    useEffect(() => {
        updateCompositionScrollbar();
    }, [
        isLoading,
        state.materials.length,
        updateCompositionScrollbar,
    ]);

    useEffect(() => {
        if (!isCalculationThumbDragging) return;

        const scrollEl = calculationScrollRef.current;
        const trackEl = calculationScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;

        const trackWidth = trackEl.clientWidth;
        const thumbWidth = calculationThumbWidth;
        const maxThumbLeft = trackWidth - thumbWidth;
        const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;

        const handlePointerMove = (e: PointerEvent) => {
            if (!calculationDragStateRef.current) return;

            const { startX, startThumbLeft } = calculationDragStateRef.current;
            const deltaX = e.clientX - startX;
            const nextThumbLeft = Math.min(Math.max(startThumbLeft + deltaX, 0), maxThumbLeft);
            let nextScrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;

            if (isNaN(nextScrollLeft)) nextScrollLeft = 0;
            setCalculationThumbLeft(nextThumbLeft);
            scrollEl.scrollLeft = nextScrollLeft;
        };

        const handlePointerUp = () => {
            setIsCalculationThumbDragging(false);
            calculationDragStateRef.current = null;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [calculationThumbWidth, isCalculationThumbDragging]);

    useEffect(() => {
        if (!isCompositionThumbDragging) return;

        const scrollEl = compositionScrollRef.current;
        const trackEl = compositionScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;

        const trackWidth = trackEl.clientWidth;
        const thumbWidth = compositionThumbWidth;
        const maxThumbLeft = trackWidth - thumbWidth;
        const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;

        const handlePointerMove = (e: PointerEvent) => {
            if (!compositionDragStateRef.current) return;

            const { startX, startThumbLeft } = compositionDragStateRef.current;
            const deltaX = e.clientX - startX;
            const nextThumbLeft = Math.min(Math.max(startThumbLeft + deltaX, 0), maxThumbLeft);
            let nextScrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;

            if (isNaN(nextScrollLeft)) nextScrollLeft = 0;
            setCompositionThumbLeft(nextThumbLeft);
            scrollEl.scrollLeft = nextScrollLeft;
        };

        const handlePointerUp = () => {
            setIsCompositionThumbDragging(false);
            compositionDragStateRef.current = null;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [compositionThumbWidth, isCompositionThumbDragging]);

    const renderLotValue = (material: RawMaterial, className: string) => {
        if (!hasChemicalComposition(material)) {
            return <span className={className}>{material.lot || "-"}</span>;
        }

        return (
            <button
                type="button"
                className={`${className} cursor-pointer underline underline-offset-2`}
                onClick={() => openCompositionDialog(material)}
                title="Show chemical composition"
            >
                {material.lot || "-"}
            </button>
        );
    };

    const MaterialRow = (index: number, material: RawMaterial) => {
        return (
            <tr key={material.raw_mtrl + material.rvid + index} className={index % 2 === 0 ? "bg-primary-dark/10 dark:bg-primary-light/10" : ""}>
                <td className={`pl-2 py-1 ${textYellowBlue}`}>{material.raw_mtrl}</td>
                <td className={textYellowBlue}>{renderLotValue(material, textYellowBlue)}</td>

                <td className={`text-right ${textYellowBlue}`}>{formatNumber(Number(material.cPercent))}</td>
                <td className={`text-right ${textYellowBlue}`}>{formatNumber(Number(material.oPercent))}</td>
                {
                    //ratio(material)
                }
                <td className={`pl-2 ${textYellowBlue}`}>{material.comment || ""}</td>
                <td className={`text-right ${textYellowBlue}`}>{formatNumber(Number(material.weightG))}</td>
                <td className={`pr-2 text-right ${textYellowBlue}`}>{Number(material.inStockGrams) * 1000}</td>
            </tr>
        );
    };

    const CancelReportDialog = () => {
        const handleCancel = async (justCancel: boolean) => {
            try {
                if (uuid) {
                    const success = await cancelRawMaterialControl(uuid);
                    if (success) {
                        showToast({ title: 'Cancelled!', intent: 'success', body: 'Report cancelled.' });
                        if (justCancel) {
                            navigate('/Oi');
                        }

                    } else {
                        showToast({ title: 'Failed', intent: 'error', body: 'Try again.' });
                    }
                }

            } catch (error) {
                console.error(error);
                showToast({ title: 'Error', intent: 'error', body: 'Error cancelling report.' });
            } finally {
                setCancelDialog(false);
            }
        };

        const handleCloneAndCancel = () => {
            handleCancel(false);
            onClone();
            setCancelDialog(false);
        }

        return (
            <Dialog open={cancelDialog} onOpenChange={(_, data) => setCancelDialog(data.open)}>
                <DialogTrigger>
                    <OiHeaderButton variant="secondary">Cancel</OiHeaderButton>
                </DialogTrigger>

                <DialogSurface>
                    {/* Close button */}
                    <button
                        onClick={() => setCancelDialog(false)}
                        className="absolute top-5 right-5 text-gray-500 hover:text-black text-lg"
                        aria-label="Close dialog"
                    >
                        ✕
                    </button>

                    <DialogBody>
                        <DialogTitle>Cancel order</DialogTitle>

                        <DialogContent>
                            <div className="flex items-center justify-end  gap-4 mt-4">
                                <Button
                                    appearance="secondary"
                                    className="w-auto"
                                    onClick={() => handleCancel(true)}
                                >
                                    Cancel order
                                </Button>

                                <Button
                                    appearance="primary"
                                    className="w-auto"
                                    onClick={handleCloneAndCancel}
                                >
                                    Clone and cancel order
                                </Button>
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        );
    };

    const DeleteReportDialog = () => {
        const handleConfirmDelete = async () => {
            try {
                if (uuid) {
                    const success = await deleteRawMaterialControl(uuid);
                    if (success) {
                        showToast({ title: 'Deleted!', intent: 'success', body: 'Report removed.' });
                        navigate('/Oi');

                    } else {
                        showToast({ title: 'Failed', intent: 'error', body: 'Try again.' });
                    }
                }

            } catch (error) {
                console.error(error);
                showToast({ title: 'Error', intent: 'error', body: 'Error deleting report.' });
            } finally {
                setDeleteDialog(false);
            }
        };

        return (
            <Dialog open={deleteDialog} onOpenChange={(_, data) => setDeleteDialog(data.open)}>
                <DialogTrigger>
                    <OiHeaderButton variant="danger">Delete</OiHeaderButton>
                </DialogTrigger>

                <DialogSurface className="">
                    <DialogBody className="h-full flex flex-col">
                        <DialogTitle>Delete Report</DialogTitle>
                        <DialogContent className="flex-1 overflow-hidden">
                            Are you sure you want to delete "{orderId}"?
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={() => setDeleteDialog(false)}>
                                Cancel
                            </Button>
                            <Button appearance="primary" onClick={handleConfirmDelete}>
                                Delete
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        );
    };

    const onClone = () => {
        navigate(`/Oi/rawMaterialControlClone/${uuid}`);
    }

    const onEdit = () => {
        navigate(`/Oi/rawMaterialControlEdit/${uuid}`);
    }
    const onPrint = () => {

        const fallbackFilename = orderId ? `Raw material control - ${orderId}.pdf` : undefined;
        DownloadRawMaterialControlPdf(uuid || "", fallbackFilename).then((response) => {
            if (!response) {
                showToast({ title: 'Failed to download PDF', intent: 'error', body: 'Try again.' });
            }
            else {
                showToast({ title: 'PDF downloaded', intent: 'success', body: 'The PDF has been downloaded successfully.' });
            }

        });
    };

    const formatDateTime = (dateTimeString: string) => {
        if (!dateTimeString) return "Not set";

        if (dateTimeString.includes(' ')) {
            dateTimeString = dateTimeString.replace(' ', 'T');
        }

        if (!dateTimeString.endsWith('Z')) {
            dateTimeString += 'Z';
        }

        try {
            const date = new Date(dateTimeString);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        } catch {
            console.error('Invalid date string:', dateTimeString);
            return dateTimeString;
        }
    };

    const achievedCarbon = state.targetPercents && state.achieved_pct ? state.targetPercents.find(tp => tp.symbol === "C")?.value : null;

    const textYellowBlue = "text-oi-button-blue dark:text-oi-powder-form-yellow truncate";
    const textGrayDark = "text-primary-dark dark:text-primary-light";
    const box = "bg-white dark:bg-oi-dark-background p-3 drop-shadow-md";
    const sortableHeaderClass = "cursor-pointer underline underline-offset-2 decoration-current";
    const calculationSummaryRowCount = state.targetPercents && state.targetPercents.length > 0 ? 3 : 0;
    const calculationButtonRowSpan = hasCalculationResults
        ? (sortedCalculationMaterials.length > 0
            ? sortedCalculationMaterials.length + calculationSummaryRowCount
            : Math.max(calculationSummaryRowCount, 1))
        : 1;

    if (isLoading) {
        return (
            <div className={`h-screen flex items-center justify-center w-full max-w-none mx-6 pb-4 ${textGrayDark}`} style={{ animation: "fadeIn 1s forwards" }}>
                <style>{fadeIn}</style>
                <Spinner />
            </div>
        );
    }

    return (
        <>
            <style>{fadeIn}</style>
            <div className={`w-full max-w-none mx-6 pb-4 testSelection ${textGrayDark}`} style={{ animation: "fadeIn 1s forwards" }}>
                <OiPageHeader
                    icon={<Box24Regular />}
                    title="Raw material control"
                    id={orderId}
                    createdAt={createdAt}
                    updatedAt={updatedAt}
                    actions={<>
                        {!isDraft && !isCancelled ? <CancelReportDialog /> : null}
                        <DeleteReportDialog />
                        {isDraft && !isCancelled ? <OiHeaderButton onClick={onEdit}>Edit</OiHeaderButton> : null}
                        <OiHeaderButton onClick={onClone}>Clone</OiHeaderButton>
                        <OiHeaderButton onClick={onPrint}>Print</OiHeaderButton>
                    </>}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                    <div className={box}>
                        <div className="text-xs font-bold mb-1">Requester:</div>
                        <div className={textYellowBlue} title={state.author}>{state.author}</div>
                    </div>
                    <div className={box}>
                        <div className="text-xs font-bold mb-1">Order id</div>
                        <div className={textYellowBlue}>{orderId}</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-2 mb-2">
                    <div className={` flex items-center`}>
                        <div className="grid grid-cols-2 max-md:grid-cols-1 gap-2 text-xs w-full">
                            <div className={box} >
                                <div className="text-xs font-bold mb-1">Title</div>
                                <div className={textYellowBlue}>{state.title ? state.title : "-"}</div>
                            </div>
                            <div className={box} title={state.comment}>
                                <div className="text-xs font-bold mb-1">Comment</div>
                                <div className={textYellowBlue}>{state.comment ? state.comment : "-"}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${box} mb-2`}>
                    <div className="text-sm font-bold mb-2">Supplier info</div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 text-xs">
                        <div>
                            <div className="text-xs font-bold mb-1">Material code from supplier</div>
                            <div className={textYellowBlue}>{state.supplierMaterialCode || "-"}</div>
                        </div>
                        <div>
                            <div className="text-xs font-bold mb-1">Batch number</div>
                            <div className={textYellowBlue}>{state.batchNumber || "-"}</div>
                        </div>
                        <div>
                            <div className="text-xs font-bold mb-1">Reg.nr</div>
                            <div className={textYellowBlue}>{state.regNr || "-"}</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-2 mb-2">
                    <div className={`${box} flex items-center`}>
                        <div className="grid grid-cols-4 gap-2 text-xs w-full">
                            <div >
                                <div className="text-xs font-bold mb-1" >Weight on mill</div>

                                <div className={textYellowBlue}>
                                    {state.weightOnMillKg ? `${state.weightOnMillKg}L` : "-"}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs font-bold mb-1">Grinding bodies</div>
                                {state.grindingBodiesType && (
                                    <div className={textYellowBlue}>{state.grindingBodiesType}</div>
                                )}
                                <div className={textYellowBlue}>
                                    {state.grindingBodies ? `${state.grindingBodies}kg` : "-"}
                                </div>
                                {state.grindingBodiesComment && (
                                    <div className={textYellowBlue}>{state.grindingBodiesComment}</div>
                                )}
                            </div>
                            <div>
                                <div className="text-xs font-bold mb-1">Grind time</div>

                                <div className={textYellowBlue}>
                                    {state.grindTimeHours != null && state.grindTimeMinutes != null
                                        ? formatGrindTime(state.grindTimeHours, state.grindTimeMinutes)
                                        : "-"}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs font-bold mb-1">Alcohol</div>
                                <div className={textYellowBlue}>
                                    {state.alcohol ? `${state.alcohol}L` : "-"}
                                </div>
                                {state.alcoholComment && (
                                    <div className={textYellowBlue}>{state.alcoholComment}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>



                {state.materials.length > 0 ? (
                    <>
                        <div className="grid grid-cols-1 gap-2">
                            <div className={`${box} mb-2`}>
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="text-xs font-bold">Composition raw material</span>
                                    <span className="text-xs">C<sub>p</sub> <span className={textYellowBlue}>{achievedCarbon !== null ? formatDecimalValue(achievedCarbon) : "-"}</span></span>
                                </div>
                                <div
                                    id="composition-scroll-area"
                                    ref={compositionScrollRef}
                                    className="relative w-full max-w-full pb-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                >
                                    <table className="w-full text-xs min-w-[480px] p-2">
                                        <thead>
                                            <tr className={textGrayDark}>
                                                <th onClick={() => handleCompositionSort("raw_mtrl")} className={`${sortableHeaderClass} py-1 text-left`}>
                                                    Raw material
                                                </th>
                                                <th onClick={() => handleCompositionSort("lot")} className={`${sortableHeaderClass} py-1 text-left`}>
                                                    Lot
                                                </th>
                                                <th onClick={() => handleCompositionSort("cPercent")} className={`${sortableHeaderClass} py-1 text-right`}>
                                                    C%
                                                </th>
                                                <th onClick={() => handleCompositionSort("oPercent")} className={`${sortableHeaderClass} py-1 text-right`}>
                                                    O%
                                                </th>
                                                <th className="py-1 text-left px-2">
                                                    Comment
                                                </th>
                                                <th onClick={() => handleCompositionSort("weightG")} className={`${sortableHeaderClass} py-1 text-right`}>
                                                    Weight (g)
                                                </th>
                                                <th onClick={() => handleCompositionSort("inStockGrams")} className={`${sortableHeaderClass} py-1 text-right`}>
                                                    In stock (g)
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="">

                                            {sortedMaterials.map((material, index) => (
                                                MaterialRow(index, material)
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="w-full mt-1">
                                    <div
                                        ref={compositionScrollbarTrackRef}
                                        className="relative h-3 rounded bg-gray-200 dark:bg-gray-700"
                                        style={{ userSelect: 'none', height: '12px' }}
                                        onPointerDown={(e) => {
                                            const scrollEl = compositionScrollRef.current;
                                            const trackEl = compositionScrollbarTrackRef.current;
                                            if (!scrollEl || !trackEl) return;

                                            const rect = trackEl.getBoundingClientRect();
                                            const clickX = e.clientX - rect.left;
                                            const trackWidth = trackEl.clientWidth;
                                            const thumbWidth = compositionThumbWidth;
                                            const maxThumbLeft = trackWidth - thumbWidth;
                                            const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;

                                            if (maxScrollLeft <= 0 || maxThumbLeft <= 0) return;

                                            const nextThumbLeft = Math.max(0, Math.min(clickX - (thumbWidth / 2), maxThumbLeft));
                                            scrollEl.scrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
                                        }}
                                    >
                                        {compositionThumbWidth > 0 && (
                                            <div
                                                role="scrollbar"
                                                aria-label="Horizontal scroll handle"
                                                aria-controls="composition-scroll-area"
                                                aria-valuenow={Math.round((compositionThumbLeft / Math.max(1, (compositionScrollbarTrackRef.current?.clientWidth || 1) - compositionThumbWidth)) * 100)}
                                                tabIndex={0}
                                                className="absolute top-0 h-3 rounded bg-oi-button-blue/90 dark:bg-oi-powder-form-yellow/90 cursor-grab active:cursor-grabbing"
                                                style={{ width: `${compositionThumbWidth}px`, transform: `translateX(${compositionThumbLeft}px)`, height: '12px' }}
                                                onPointerDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    compositionDragStateRef.current = {
                                                        startX: e.clientX,
                                                        startThumbLeft: compositionThumbLeft,
                                                    };
                                                    setIsCompositionThumbDragging(true);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>


                    </>
                ) : null}
                <Dialog open={Boolean(selectedCompositionMaterial)} onOpenChange={(_, data) => {
                    if (!data.open) {
                        setSelectedCompositionMaterial(null);
                    }
                }}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>
                                Lot Details{selectedCompositionMaterial?.lot ? `: ${selectedCompositionMaterial.lot}` : ""}
                            </DialogTitle>
                            <DialogContent>
                                {selectedCompositionMaterial?.powderComposition ? (
                                    <PowderPieChart data={selectedCompositionMaterial.powderComposition} />
                                ) : null}
                            </DialogContent>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                    <div className={box}>
                        <div className="text-sm font-bold mb-2">
                            {state.isUsedForInserts ? "Powder will be used for inserts" : "Powder will not be used for inserts"}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                                <div className="text-xs font-bold mb-1">Geometry</div>
                                <div className={textYellowBlue}>{state.geometry}</div>
                            </div>
                            <div>
                                <div className="text-xs font-bold mb-1" >Amount</div>
                                <div className={textYellowBlue}>{state.amount}</div>
                            </div>
                            <div>
                                <div className="text-xs font-bold mb-1" >Press pressure</div>
                                <div className={textYellowBlue}>{state.pressPressure}</div>
                            </div>
                        </div>
                    </div>
                    <div className={box}>
                        <div className="text-sm font-bold mb-2">Sintering {state.furnaceType === FurnaceType.LAB ? "- Lab furnace" : " - Production furnace"}</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                                <div className="text-xs font-bold mb-1">Sintering code</div>
                                <div className={textYellowBlue}>{state.sinteringCode}</div>
                            </div>
                            <div>
                                <div className="text-xs font-bold mb-1">Sintering temperature</div>
                                <div className={textYellowBlue}>{state.sinteringTemperature}° C</div>
                            </div>
                        </div>
                    </div>
                </div>





                <div className={`${box} mb-2`}>
                    <span className="text-sm font-bold">Tags: </span>
                    {state.tags.length === 0 ? (
                        <span className={textYellowBlue}>No tags available.</span>
                    ) : (
                        <span className={textYellowBlue}>{state.tags.map(tag => tag).join(", ")}</span>
                    )}
                </div>


                <div className={`${box} mb-2`}>
                    <span className="text-sm font-bold">Tagged staff: </span>
                    {state.taggedStaff.length === 0 ? (
                        <span className={textYellowBlue}>No staff tagged.</span>
                    ) : (
                        <span className={textYellowBlue}>{state.taggedStaff.join(", ")}</span>
                    )}
                </div>


                <div className={`${box} mb-2`}>
                    <span className="text-sm font-bold">Attachments: </span>
                    {state.uploadedFiles.length > 0 ? (
                        <div className="space-y-1">
                            {state.uploadedFiles.map((file, index) => {
                                return (
                                    <div key={index}>

                                        <button
                                            onClick={() => {
                                                try {
                                                    onOpenFileGallery?.(state.uploadedFiles, index);
                                                } catch (error) {
                                                    console.error('Error opening file:', error);
                                                    showToast({ title: 'Error opening file', intent: 'error', body: `Could not open file: ${file}` });
                                                }
                                            }}
                                            className="text-yellow-400 hover:text-yellow-300 underline bg-transparent border-none cursor-pointer p-0 text-left text-sm"
                                            title={`Open ${file.fileName}`}
                                        >
                                            {file.fileName}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <span className="text-yellow-400 text-sm">No attachments</span>
                    )}

                </div>

            </div>
        </>
    );
}
